import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { PlanProvider } from './context/PlanContext';
import { FacilityProvider } from './context/FacilityContext';
import PlanGate from './olli/components/PlanGate';
import ErrorBoundary from './components/ErrorBoundary';
import { captureUTMParameters } from './utils/utmTracking';
import Login from './olli/screens/auth/SignIn';
import Register from './olli/screens/auth/Signup';
import ForgotPassword from './olli/screens/auth/ForgotPassword';
import Welcome from './olli/screens/auth/Welcome';
import Notifications from './olli/screens/Notifications';
import ResetPassword from './olli/screens/auth/ResetPassword';
import Dashboard from './pages/Dashboard';
// Ads & Marketing suite pages
import MarketingDashboard from './olli/screens/marketing/MarketingDashboard';
import PagePerformance from './olli/screens/marketing/PagePerformance';
import AdCampaignsList from './olli/screens/marketing/AdCampaigns';
import AdCampaignDetail from './olli/screens/marketing/CampaignView';
import CreateCampaign from './olli/screens/marketing/CreateCampaign';
import LeadForms from './olli/screens/marketing/LeadForms';
import Audiences from './olli/screens/marketing/Audiences';
import ComposePost from './olli/screens/marketing/ComposePost';
import Comments from './olli/screens/marketing/Comments';
import Instagram from './olli/screens/marketing/Instagram';
import CompetitorResearch from './olli/screens/marketing/CompetitorResearch';
import OptimizationRules from './olli/screens/marketing/OptimizationRules';
// Social messaging suite (Bundle E)
import SocialInbox from './olli/screens/marketing/SocialInbox';
import Automations from './olli/screens/marketing/Automations';
import AutomationsDetail from './olli/components/marketing/AutomationsDetail';
// Public compliance page (Meta App Review prereq P4)
import DataDeletion from './olli/screens/DataDeletion';
import PlansPage from './olli/screens/Plans';
import MySubscription from './olli/screens/MySubscription';
import FacilitiesList from './olli/screens/settings/Facilities';
import BrandingSettings from './olli/screens/settings/Branding';
import FacilityForm from './olli/screens/settings/Facilities';
import FacilityDepartments from './olli/screens/settings/Departments';
import StaffList from './olli/screens/settings/Staff';
import StaffForm from './olli/screens/settings/Staff';
import SpecialtiesManagement from './olli/screens/settings/Specialties';
import LeadStatusManagement from './olli/screens/settings/LeadStatuses';
import LeadPivot from './olli/screens/crm/LeadReports';
import LeadsList from './olli/screens/crm/Leads';
import LeadForm from './olli/screens/crm/LeadForm';
import LeadDetails from './olli/screens/crm/LeadView';
import ContactsList from './olli/screens/crm/Contacts';
import ContactForm from './olli/screens/crm/ContactForm';
import ContactDetails from './olli/screens/crm/ContactView';
import TasksList from './olli/screens/crm/Tasks';
import ScoringRules from './olli/screens/crm/ScoringRules';
import AssignmentRules from './olli/screens/crm/AssignmentRules';
import TagsManagement from './olli/screens/crm/Tags';
import IntegrationsList from './olli/screens/settings/Integrations';
import SyncApiKeys from './olli/screens/settings/ApiKeys';
import SyncApiDocs from './olli/screens/settings/SyncApiDocs';
import IntegrationDetails from './olli/screens/settings/IntegrationDetail';
import ConnectMeta from './olli/screens/settings/ConnectMeta';
import WebhookSetup from './olli/screens/settings/WebhookSetup';
import WebhookLogs from './olli/screens/settings/WebhookLogs';
import MetaConversionsSetup from './olli/screens/settings/MetaConversionsSetup';
import MetaConversionsGuide from './olli/screens/settings/MetaConversionsGuide';
import ConnectGoogle from './olli/screens/settings/ConnectGoogle';
import ConnectWhatsApp from './olli/screens/settings/ConnectWhatsApp';
import PatientsList from './olli/screens/patients/Patients';
import PatientForm from './olli/screens/patients/PatientForm';
import PatientDetails from './olli/screens/patients/PatientView';
import AppointmentsList from './olli/screens/Appointments';
import AppointmentForm from './olli/screens/Appointments';
import AppointmentDetails from './olli/screens/Appointments';
import SchedulesPage from './olli/screens/Schedules';
import WABADashboard from './olli/screens/whatsapp/Overview';
import ConversationsList from './olli/screens/whatsapp/Conversations';
import ConversationView from './olli/screens/whatsapp/Conversations';
import { TemplatesList } from './olli/screens/whatsapp/Templates';
import { TemplateForm } from './olli/screens/whatsapp/Templates';
import { TemplateForm as TemplateView } from './olli/screens/whatsapp/Templates';
import { CampaignList as CampaignsList } from './olli/screens/whatsapp/Campaigns';
import { CampaignForm } from './olli/screens/whatsapp/Campaigns';
import { CampaignView as CampaignDetails } from './olli/screens/whatsapp/Campaigns';
import ApiDocumentation from './olli/screens/whatsapp/ApiDocs';
import WABASettings from './olli/screens/whatsapp/WebhookForwarder';
import Home from './olli/screens/Home';
import Wallet from './olli/screens/Wallet';
import WalletHistory from './olli/screens/WalletHistory';
import WalletPricing from './olli/screens/WalletPricing';
import KnowledgeBaseDashboard from './olli/screens/ai-agents/KnowledgeBase';
import AskOlli from './olli/screens/AskOlli';
import useFeatureFlags from './hooks/useFeatureFlags';
import Complaints from './olli/screens/support/Complaints';
import ComplaintDetail from './olli/screens/support/ComplaintDetail';
import SupportDashboard from './olli/screens/support/Dashboard';
import Feedback from './olli/screens/support/Feedback';
import AIAgentConfig from './olli/screens/ai-agents/Agents';
import AIAgentTemplates from './olli/screens/ai-agents/Templates';
import TreatmentTypesList from './olli/screens/treatments/TreatmentTypes';
import TreatmentStatusManagement from './olli/screens/treatments/StatusDefinitions';
import InstructionTemplatesList from './olli/screens/treatments/InstructionTemplates';
import InstructionTemplateForm from './olli/screens/treatments/InstructionTemplateForm';
import TreatmentPlansList from './olli/screens/treatments/AllPlans';
import TreatmentPlanForm from './olli/screens/treatments/AllPlans';
import TreatmentPlanDetails from './olli/screens/treatments/PlanView';
import './App.css';

// Ask Olli is switched off on some servers (flags.ask_olli false, production
// for now): its page then goes to the dashboard instead of opening.
function AskOlliGate({ children }) {
  const { flags, loading } = useFeatureFlags();
  if (loading) return null;
  return flags.ask_olli === true ? children : <Navigate to="/dashboard" replace />;
}

// Capture UTM parameters immediately when module loads (before any routing)
// This ensures we capture the original URL before React Router redirects
if (typeof window !== 'undefined') {
  captureUTMParameters();
}

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
};

// Public Route component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return !token ? children : <Navigate to="/dashboard" />;
};

function App() {
  return (
    <Router>
      <PlanProvider>
      <FacilityProvider>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register/*"
          element={<Register />}
        />
        <Route
          path="/welcome"
          element={
            <ProtectedRoute>
              <Welcome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Dashboard><Notifications /></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Public compliance page — no auth, accessible without login */}
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard><ErrorBoundary><Home /></ErrorBoundary></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/facilities"
          element={
            <ProtectedRoute>
              <Dashboard><FacilitiesList /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/branding"
          element={
            <ProtectedRoute>
              <Dashboard><BrandingSettings /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/specialties"
          element={
            <ProtectedRoute>
              <Dashboard><SpecialtiesManagement /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/lead-statuses"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="crm_leads"><LeadStatusManagement /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/facilities/new"
          element={
            <ProtectedRoute>
              <Dashboard><FacilityForm /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/facilities/edit/:id"
          element={
            <ProtectedRoute>
              <Dashboard><FacilityForm /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/departments"
          element={
            <ProtectedRoute>
              <Dashboard><FacilityDepartments /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/staff"
          element={
            <ProtectedRoute>
              <Dashboard><StaffList /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/staff/new"
          element={
            <ProtectedRoute>
              <Dashboard><StaffForm /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/staff/edit/:id"
          element={
            <ProtectedRoute>
              <Dashboard><StaffForm /></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* CRM - Leads Routes */}
        <Route
          path="/crm/leads"
          element={
            <ProtectedRoute>
              <Dashboard><LeadsList /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads/reports"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="crm_leads"><LeadPivot /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads/new"
          element={
            <ProtectedRoute>
              <Dashboard><LeadForm /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads/edit/:id"
          element={
            <ProtectedRoute>
              <Dashboard><LeadForm /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads/:id"
          element={
            <ProtectedRoute>
              <Dashboard><LeadDetails /></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* CRM - Contacts Routes */}
        <Route
          path="/crm/contacts"
          element={
            <ProtectedRoute>
              <Dashboard><ContactsList /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/contacts/new"
          element={
            <ProtectedRoute>
              <Dashboard><ContactForm /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/contacts/edit/:id"
          element={
            <ProtectedRoute>
              <Dashboard><ContactForm /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/contacts/:id"
          element={
            <ProtectedRoute>
              <Dashboard><ContactDetails /></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* CRM - Tasks */}
        <Route
          path="/crm/tasks"
          element={
            <ProtectedRoute>
              <Dashboard><TasksList /></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* CRM - Settings Routes */}
        <Route
          path="/crm/settings/scoring-rules"
          element={
            <ProtectedRoute>
              <Dashboard><ScoringRules /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/settings/assignment-rules"
          element={
            <ProtectedRoute>
              <Dashboard><AssignmentRules /></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/settings/tags"
          element={
            <ProtectedRoute>
              <Dashboard><TagsManagement /></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Ads & Marketing suite routes */}
        <Route
          path="/marketing/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><MarketingDashboard /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/page-performance"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><PagePerformance /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads/campaigns"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><AdCampaignsList /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads/campaigns/new"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><CreateCampaign /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads/campaigns/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><AdCampaignDetail /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads/lead-forms"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><LeadForms /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads/audiences"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><Audiences /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/content/compose"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><ComposePost /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/content/comments"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><Comments /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/instagram"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><Instagram /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/research/competitors"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><CompetitorResearch /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads/rules"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><OptimizationRules /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Social messaging suite routes (Bundle E) */}
        <Route
          path="/social-inbox"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><SocialInbox /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/automations"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><Automations /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/automations/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="marketing_suite"><ErrorBoundary><AutomationsDetail /></ErrorBoundary></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Settings - Integrations Routes */}
        {/* Declared before /settings/integrations/:id so "api-keys" is not
            captured as an integration id. */}
        <Route
          path="/settings/integrations/api-docs"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><SyncApiDocs /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/api-keys"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><SyncApiKeys /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><IntegrationsList /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><IntegrationDetails /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/:id/connect-meta"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><ConnectMeta /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/:id/connect-google"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><ConnectGoogle /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/:id/connect-whatsapp"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><ConnectWhatsApp /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/:id/webhook-setup"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><WebhookSetup /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/:id/logs"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><WebhookLogs /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/meta-conversions"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><MetaConversionsSetup /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/integrations/meta-conversions/guide"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="integrations"><MetaConversionsGuide /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Patients Routes */}
        <Route
          path="/patients"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="patients"><PatientsList /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/patients/new"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="patients"><PatientForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/patients/edit/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="patients"><PatientForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/patients/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="patients"><PatientDetails /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Appointments Routes */}
        <Route
          path="/appointments"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="appointments"><AppointmentsList /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/appointments/new"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="appointments"><AppointmentForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/appointments/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="appointments"><AppointmentDetails /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Schedules Routes (Facility & Doctor) */}
        <Route
          path="/schedules"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="schedules"><SchedulesPage /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* WABA Routes */}
        <Route
          path="/waba"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><WABADashboard /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/conversations"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><ConversationsList /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/conversations/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><ConversationView /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/templates"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><TemplatesList /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/templates/new"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><TemplateForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/templates/:id/view"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><TemplateView /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/templates/:id/edit"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><TemplateForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/campaigns"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><CampaignsList /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/campaigns/new"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><CampaignForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/campaigns/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><CampaignDetails /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/campaigns/:id/edit"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><CampaignForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/api-docs"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><ApiDocumentation /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/waba/settings"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><WABASettings /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Wallet Routes */}
        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><Wallet /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet/transactions"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><WalletHistory /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet/pricing"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="whatsapp"><WalletPricing /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Knowledge Base Routes */}
        <Route
          path="/knowledge-base"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="knowledge_base"><KnowledgeBaseDashboard /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ask-olli"
          element={
            <ProtectedRoute>
              <AskOlliGate><Dashboard><PlanGate module="knowledge_base"><AskOlli /></PlanGate></Dashboard></AskOlliGate>
            </ProtectedRoute>
          }
        />
        {/* AI Agent */}
        <Route
          path="/settings/ai-agent/templates"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="ai_agents_chat,ai_agents_voice"><AIAgentTemplates /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/ai-agent"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="ai_agents_chat,ai_agents_voice"><AIAgentConfig /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Treatment Settings Routes */}
        <Route
          path="/settings/treatments"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="treatment_plans"><TreatmentTypesList /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/treatment-statuses"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="treatment_plans"><TreatmentStatusManagement /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/instruction-templates"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="treatment_plans"><InstructionTemplatesList /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/instruction-templates/new"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="treatment_plans"><InstructionTemplateForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/instruction-templates/edit/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="treatment_plans"><InstructionTemplateForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Treatment Plans Routes */}
        <Route
          path="/treatment-plans"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="treatment_plans"><TreatmentPlansList /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/treatment-plans/new"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="treatment_plans"><TreatmentPlanForm /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/treatment-plans/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="treatment_plans"><TreatmentPlanDetails /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Support Routes */}
        <Route
          path="/support/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="support"><SupportDashboard /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/complaints/:id"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="support"><ComplaintDetail /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/complaints"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="support"><Complaints /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/feedback"
          element={
            <ProtectedRoute>
              <Dashboard><PlanGate module="support"><Feedback /></PlanGate></Dashboard>
            </ProtectedRoute>
          }
        />
        {/* Subscription Pages */}
        <Route path="/subscription/plans" element={<ProtectedRoute><Dashboard><PlansPage /></Dashboard></ProtectedRoute>} />
        <Route path="/subscription/manage" element={<ProtectedRoute><Dashboard><MySubscription /></Dashboard></ProtectedRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
      </FacilityProvider>
      </PlanProvider>
    </Router>
  );
}

export default App;
